/**
 * The card surface, as components.
 *
 * The root CLAUDE.md documents a hand-written card recipe, and most feature
 * code writes it out by hand rather than importing anything — which is why a
 * card redesign previously meant editing dozens of files. These exist so that
 * the recipe has somewhere to live: change the card here, and every card
 * changes.
 *
 * The surface is `--nf-ds-surface-card` and a radius, and nothing else draws
 * its edge. It carried `ring-1 ring-nf-ds-surface-card-ring ring-inset` on the
 * argument that a ring paints over the fill where a border would inset it by a
 * pixel — true, and beside the point, because the guidelines do not ask for a
 * thinner line, they ask for none: "A card is a fill and a radius. No border,
 * no ring, no shadow line", and `surface-card-ring` is "for controls only ...
 * Not on a card". At a 28px radius a drawn line is what makes a soft shape
 * read as a box.
 *
 * What separates the pane from its ground now is the pane shadow, which is
 * what the guidelines' own example uses. In light the pane is opaque, in dark
 * translucent — the flip lives in the token layer, so there are no `dark:`
 * classes here.
 *
 * @example
 * <Card>
 *   <CardHeader
 *     title="Batch configuration"
 *     description="Testing a brand-forward static direction."
 *     end={<Badge variant="ok">Running</Badge>}
 *   />
 *   <CardRows>
 *     <FieldRow label="Platform">Meta Ads</FieldRow>
 *     <FieldRow label="Target CPA">$100.00</FieldRow>
 *   </CardRows>
 * </Card>
 *
 * Spec: packages/nf-design-system/colors_and_type.css (Card surface, Radius
 * ROLES, Spacing ROLES)
 */
import type { ReactNode } from "react";
import { cn } from "@/lib/utils";
import { Body, Rail, Title } from "./text";

/** The pane: surface, radius, padding and hairline, all from roles. */
export function Card({
  children,
  className,
}: {
  children: ReactNode;
  className?: string;
}) {
  return (
    <div
      className={cn(
        "rounded-nf-ds-card bg-nf-ds-surface-card p-nf-ds-card",
        "shadow-nf-ds-pane",
        className,
      )}
    >
      {children}
    </div>
  );
}

/**
 * Title, optional description, optional end slot.
 *
 * The end slot wraps rather than squeezing: in a narrow column the title would
 * otherwise shrink until the heading broke mid-phrase, which is worse than the
 * status pill moving to its own line.
 */
export function CardHeader({
  title,
  description,
  end,
  className,
}: {
  title: ReactNode;
  description?: ReactNode;
  /** Right of the title — a status pill, a count, an action. */
  end?: ReactNode;
  className?: string;
}) {
  return (
    <header
      className={cn(
        "flex flex-wrap items-start justify-between gap-nf-ds-item",
        className,
      )}
    >
      <div className="flex min-w-0 flex-col gap-nf-ds-tight">
        <Title>{title}</Title>
        {description ? <Body muted>{description}</Body> : null}
      </div>
      {end ? <div className="shrink-0">{end}</div> : null}
    </header>
  );
}

/**
 * A hairline-separated list of rows inside a card.
 *
 * A `<dl>`, because `FieldRow` emits `<dt>` and `<dd>` and those are only
 * meaningful inside a description list. Rendering this as a plain `<div>` made
 * the documented composition produce invalid markup, and assistive technology
 * stops pairing the term with its value.
 *
 * Pulls itself out to the card's padding so the rules reach the pane's edges
 * while the content stays aligned with the header above it.
 */
export function CardRows({
  children,
  className,
}: {
  children: ReactNode;
  className?: string;
}) {
  return (
    <dl
      className={cn(
        "-mx-nf-ds-card mt-nf-ds-block divide-y divide-border border-y border-border",
        className,
      )}
    >
      {children}
    </dl>
  );
}

/**
 * One label/value row.
 *
 * `stacked` puts the value under the label, for values too long to sit beside
 * one — a full ad-account id in a narrow column, say.
 */
export function FieldRow({
  label,
  children,
  stacked = false,
  align = "center",
  className,
}: {
  label: ReactNode;
  children: ReactNode;
  stacked?: boolean;
  /** `start` when the value is a paragraph rather than a single line. */
  align?: "center" | "start";
  className?: string;
}) {
  return (
    <div
      className={cn(
        "px-nf-ds-card py-nf-ds-block",
        stacked
          ? "flex flex-col gap-nf-ds-tight"
          : "flex flex-wrap justify-between gap-nf-ds-item",
        !stacked && align === "start" ? "items-start" : "",
        !stacked && align === "center" ? "items-baseline" : "",
        className,
      )}
    >
      <Rail as="dt">{label}</Rail>
      <Body as="dd" className={stacked ? undefined : "min-w-0 text-right"}>
        {children}
      </Body>
    </div>
  );
}
